import React, { useState } from "react";
import { Eye, EyeOff, AlertCircle, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import logo from "../assets/logo.png";
import {
  fetchOrionProfile,
  isOrionUsername,
  loginOrionUser,
  normalizeOrionUser,
} from "../amandev-api/orionAuth";

// Modern Centered LoginForm Component with inline styles
/**
 * U.S. Government system-use notification.
 *
 * Standard federal consent banner, shown before authentication rather than after,
 * because the consent it records is consent to *access* the system. The headline
 * warning is always visible; the detailed consent terms expand, so the notice is
 * complete without burying the sign-in form below a wall of text.
 *
 * NOTE: this is the common federal boilerplate. Agencies publish their own
 * approved wording (agency-specific wording included) — have your OCIO or counsel confirm the
 * exact text before this goes anywhere real.
 */
const SystemUseNotice = () => {
  const [expanded, setExpanded] = useState(false);
  return (
    <div style={{
      maxWidth: '560px', margin: '0 auto 20px auto',
      border: '1px solid var(--cares-orange)', borderLeft: '5px solid var(--cares-orange)',
      borderRadius: '10px', background: 'color-mix(in srgb, var(--cares-orange) 14%, var(--cares-surface-card))', overflow: 'hidden',
    }}>
      <div style={{ padding: '14px 16px' }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px',
        }}>
          <AlertCircle size={16} style={{ color: 'var(--cares-orange)', flexShrink: 0 }} />
          <span style={{
            fontSize: '12px', fontWeight: 700, letterSpacing: '0.06em',
            textTransform: 'uppercase', color: 'var(--cares-orange)',
          }}>
            Warning — U.S. Government Information System
          </span>
        </div>

        <p style={{ fontSize: '12px', lineHeight: 1.6, color: 'var(--cares-orange)', margin: 0 }}>
          You are accessing a U.S. Government information system, which includes
          this computer, this computer network, all computers connected to this
          network, and all devices and storage media attached to this network or to
          a computer on this network. This information system is provided for
          U.S. Government-authorized use only. Unauthorized or improper use of this
          system may result in disciplinary action, as well as civil and criminal
          penalties.
        </p>

        {expanded && (
          <div style={{ marginTop: '10px' }}>
            <p style={{
              fontSize: '12px', lineHeight: 1.6, color: 'var(--cares-orange)',
              margin: '0 0 6px 0', fontWeight: 600,
            }}>
              By using this information system, you understand and consent to the
              following:
            </p>
            <ul style={{
              margin: 0, paddingLeft: '18px', fontSize: '12px',
              lineHeight: 1.6, color: 'var(--cares-orange)',
            }}>
              <li>
                You have no reasonable expectation of privacy regarding any
                communication or data transiting or stored on this information
                system.
              </li>
              <li>
                At any time, and for any lawful Government purpose, the Government
                may monitor, intercept, search and seize any communication or data
                transiting or stored on this information system.
              </li>
              <li>
                Any communication or data transiting or stored on this information
                system may be disclosed or used for any lawful Government purpose.
              </li>
              <li>
                This system is monitored to ensure proper functioning, to protect
                against unauthorized access, and to verify security procedures and
                operational security.
              </li>
            </ul>
          </div>
        )}

        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          style={{
            marginTop: '10px', background: 'none', border: 'none', padding: 0,
            color: 'var(--cares-orange)', fontSize: '12px', fontWeight: 600,
            textDecoration: 'underline', cursor: 'pointer',
          }}
        >
          {expanded ? 'Hide the full notice' : 'Read the full notice'}
        </button>
      </div>
    </div>
  );
};

const LoginForm = ({
  username,
  password,
  showPassword,
  loading,
  error,
  setUsername,
  setPassword,
  setShowPassword,
  handleLogin,
}) => (
  <div style={{
    minHeight: '100vh',
    background: 'linear-gradient(135deg, var(--cares-surface-board) 0%, color-mix(in srgb, var(--cares-blue) 14%, var(--cares-surface-card)) 50%, color-mix(in srgb, var(--cares-purple) 14%, var(--cares-surface-card)) 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px'
  }}>
    <div style={{ width: '100%', maxWidth: '560px' }}>
      {/* Consent notice precedes the form: it governs access, not what you do
          once inside. */}
      <SystemUseNotice />

      {/* Login Card - Compact and Centered */}
      <div style={{
        background: 'var(--cares-surface-card)',
        borderRadius: '16px',
        boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)',
        padding: '40px 32px 32px 32px',
        border: '1px solid var(--cares-line-card)',
        maxWidth: '380px',
        margin: '0 auto'
      }}>
        {/* Logo Section - Inside Box */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
            <img
              src={logo}
              alt="AIEA Logo"
              style={{ height: '64px', width: 'auto' }}
            />
          </div>
          <h1 style={{
            fontSize: '24px',
            fontWeight: 'bold',
            color: 'var(--cares-text-1)',
            marginBottom: '4px'
          }}>
            Welcome to AIEA
          </h1>
          <p style={{ color: 'var(--cares-text-label)', fontSize: '13px' }}>
            AI-Enabled Automation Platform
          </p>
        </div>

        <div style={{ marginBottom: '24px' }}>
          <h2 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: 'var(--cares-text-2)',
            textAlign: 'center'
          }}>
            Sign In
          </h2>
          <p style={{
            color: 'var(--cares-text-note)',
            textAlign: 'center',
            marginTop: '4px',
            fontSize: '13px'
          }}>
            Enter your credentials
          </p>
        </div>

        {error && (
          <div style={{
            marginBottom: '16px',
            background: 'var(--cares-surface-board)',
            borderLeft: '4px solid var(--cares-orange)',
            padding: '12px',
            borderRadius: '0 4px 4px 0'
          }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <AlertCircle style={{
                height: '16px',
                width: '16px',
                color: 'var(--cares-orange)',
                marginRight: '8px',
                flexShrink: 0
              }} />
              <span style={{ color: 'var(--cares-orange)', fontSize: '12px' }}>{error}</span>
            </div>
          </div>
        )}

        <form onSubmit={handleLogin}>
          {/* Username Input */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              fontSize: '11px',
              fontWeight: '600',
              color: 'var(--cares-text-3)',
              marginBottom: '6px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Email Address
            </label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '2px solid var(--cares-line-card)',
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none',
                transition: 'all 0.2s',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = 'var(--cares-blue)';
                e.target.style.boxShadow = '0 0 0 3px rgba(66, 153, 225, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'var(--cares-line-card)';
                e.target.style.boxShadow = 'none';
              }}
              placeholder="your.email@example.com"
              required
            />
          </div>

          {/* Password Input */}
          <div style={{ marginBottom: '24px' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '6px'
            }}>
              <label style={{
                fontSize: '11px',
                fontWeight: '600',
                color: 'var(--cares-text-3)',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Password
              </label>
              <a href="#forgot" style={{
                fontSize: '11px',
                color: 'var(--cares-blue)',
                fontWeight: '500',
                textDecoration: 'none'
              }}>
                Forgot?
              </a>
            </div>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 40px 10px 12px',
                  border: '2px solid var(--cares-line-card)',
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none',
                  transition: 'all 0.2s',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = 'var(--cares-blue)';
                  e.target.style.boxShadow = '0 0 0 3px rgba(66, 153, 225, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'var(--cares-line-card)';
                  e.target.style.boxShadow = 'none';
                }}
                placeholder="••••••••"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--cares-text-note)',
                  padding: '4px'
                }}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {/* Sign In Button */}
          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              background: loading ? 'var(--cares-surface-raised)' : 'linear-gradient(135deg, var(--cares-blue) 0%, var(--cares-purple) 100%)',
              color: 'var(--cares-text-max)',
              padding: '12px',
              borderRadius: '8px',
              fontWeight: '600',
              fontSize: '13px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              border: 'none',
              cursor: loading ? 'not-allowed' : 'pointer',
              boxShadow: '0 4px 6px rgba(66, 153, 225, 0.3)',
              transition: 'all 0.2s',
              marginTop: '8px'
            }}
            onMouseEnter={(e) => {
              if (!loading) {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 12px rgba(66, 153, 225, 0.4)';
              }
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 6px rgba(66, 153, 225, 0.3)';
            }}
          >
            {loading ? "Signing in..." : "Sign In"}
          </button>
          <p style={{
            marginTop: '12px', fontSize: '11px', lineHeight: 1.5,
            color: 'var(--cares-text-label)', textAlign: 'center',
          }}>
            By signing in you acknowledge and consent to the terms of the
            U.S. Government system-use notice above.
          </p>
        </form>

        {/* Divider */}
        <div style={{ position: 'relative', margin: '24px 0' }}>
          <div style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center'
          }}>
            <div style={{ width: '100%', borderTop: '1px solid var(--cares-line-card)' }}></div>
          </div>
          <div style={{
            position: 'relative',
            display: 'flex',
            justifyContent: 'center',
            fontSize: '12px'
          }}>
            <span style={{
              padding: '0 8px',
              background: 'var(--cares-surface-card)',
              color: 'var(--cares-text-note)'
            }}>
              New to AIEA?
            </span>
          </div>
        </div>

        {/* Register Button */}
        <button
          type="button"
          style={{
            width: '100%',
            border: '2px solid var(--cares-line-strong)',
            background: 'var(--cares-surface-card)',
            color: 'var(--cares-text-3)',
            padding: '10px',
            borderRadius: '8px',
            fontWeight: '600',
            fontSize: '13px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            e.target.style.background = 'var(--cares-surface-board)';
            e.target.style.borderColor = 'var(--cares-line-strong)';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'var(--cares-surface-card)';
            e.target.style.borderColor = 'var(--cares-line-strong)';
          }}
        >
          Create Account
        </button>

        {/* Footer */}
        <div style={{ marginTop: '24px', textAlign: 'center' }}>
          <p style={{ fontSize: '11px', color: 'var(--cares-text-note)' }}>
            🔒 Secured by AWS Cognito
          </p>
        </div>
      </div>

      {/* Additional Info */}
      <div style={{ marginTop: '16px', textAlign: 'center' }}>
        <p style={{ fontSize: '12px', color: 'var(--cares-text-label)' }}>
          Need help?{' '}
          <a href="#support" style={{
            color: 'var(--cares-blue)',
            fontWeight: '500',
            textDecoration: 'none'
          }}>
            Contact Support
          </a>
        </p>
      </div>
    </div>
  </div>
);

// MFAForm Component (unchanged)
const MFAForm = ({
  mfaCode,
  error,
  loading,
  setMfaCode,
  handleMFA,
  userInfo,
}) => {
  const [showModal, setShowModal] = useState(false);
  
  // Static data that would come from the API
  const mfaSetupData = {
    mfa_manual: {
      authenticator_app: {
        title: "Set Up Using Authenticator App",
        steps: [
          "Open your account settings and go to 'Security' → 'Multi-Factor Authentication'.",
          "Choose 'Authenticator App' as your second factor.",
          "Scan the QR code using your preferred authenticator app (Google, Microsoft, Duo).",
          "If unable to scan, manually enter the secret key displayed.",
          "Enter the 6-digit code generated by the app to verify setup.",
          "Save your backup codes in a secure place.",
          "Click 'Confirm' to activate the authenticator for your account."
        ],
        note: "Ensure your phone's time is accurate for the code to work correctly."
      },
      smart_card: {
        title: "Set Up Using Smart Card (CAC/PIV)",
        steps: [
          "Connect your smart card reader to your device.",
          "Insert your CAC or PIV card into the reader.",
          "Go to your profile → 'Security Settings' → 'Multi-Factor Authentication'.",
          "Select 'Smart Card (CAC/PIV)' as your authentication method.",
          "Choose your authentication certificate from the card.",
          "Enter your PIN to validate your card.",
          "Your smart card is now linked to your account for future logins."
        ],
        note: "Make sure drivers and middleware like ActivClient are installed if using Windows."
      }
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gray-800" style={{ backgroundColor: "var(--cares-surface-page)" }}>
      {/* Modal for MFA Setup Instructions */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-gray-800">MFA Setup Instructions</h2>
                <button 
                  onClick={() => setShowModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              
              <div className="space-y-8">
                {/* Authenticator App Section */}
                <div className=" p-4 rounded-lg">
                  <h3 className="text-xl font-semibold text-blue-800 mb-3 flex justify-center">
                    {mfaSetupData.mfa_manual.authenticator_app.title}
                  </h3>
                  <br></br>
                  <ol className="list-decimal pl-5 space-y-2 text-gray-700">
                    {mfaSetupData.mfa_manual.authenticator_app.steps.map((step, index) => (
                      <li key={index} className="pb-2">{step}</li>
                    ))}
                  </ol>
                  <div className="mt-3 p-3 rounded">
                    <p className="text-sm text-blue-800">
                      <span className="font-semibold">Note:</span> {mfaSetupData.mfa_manual.authenticator_app.note}
                    </p>
                  </div>
                </div>
                
                {/* Smart Card Section */}
                <div className="bg-purple-50 p-4 rounded-lg">
                  <h3 className="text-xl font-semibold text-purple-800 mb-3 flex justify-center">
                    {mfaSetupData.mfa_manual.smart_card.title}
                  </h3>
                  <br></br>
                  <ol className="list-decimal pl-5 space-y-2 text-gray-700">
                    {mfaSetupData.mfa_manual.smart_card.steps.map((step, index) => (
                      <li key={index} className="pb-2">{step}</li>
                    ))}
                  </ol>
                  <div className="mt-3 p-3 bg-purple-100 rounded">
                    <p className="text-sm text-purple-800">
                      <span className="font-semibold">Note:</span> {mfaSetupData.mfa_manual.smart_card.note}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="mt-6 flex justify-end justify-center">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-md mx-auto">
        <div className="text-center mb-8">
          <div className="flex justify-center">
            <div className=" p-1 rounded-full transition-all duration-300">
              <img
                src="/aiea-logo-bright.png"
                alt="AIEA Logo"
                 className="w-full"
              style={{ height: "128%" }}
              />
            </div>
          </div>
          <h1 className="text-3xl font-light text-white mt-4 mb-2">
            Multi-Factor Authentication
          </h1>
          <h2 className="text-2xl font-light text-white mb-6">
            Welcome {userInfo?.name || "User"}
          </h2>

          <div 
            className="bg-gray-700 p-4 rounded-lg mb-6 cursor-pointer hover:bg-gray-600 transition-colors"
            onClick={() => setShowModal(true)}
          >
            <h3 className="font-medium text-white text-center">How to set up</h3>
          </div>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 p-3 rounded-lg">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
              <span className="text-red-700 text-sm">{error}</span>
            </div>
          </div>
        )}

        <div className="space-y-6 flex flex-col items-center">
          <div className="w-2/5">
            <input
              type="text"
              value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value)}
              className="w-full px-4 py-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center text-lg tracking-widest"
              placeholder="Enter CAC/PIV Code"
              maxLength="6"
              required
            />
          </div>

          <button
            onClick={handleMFA}
            disabled={loading}
            className="w-2/5 bg-blue-600 text-white py-4 rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Verifying..." : "Confirm"}
          </button>

          
        </div>
      </div>
    </div>
  );
};

// Main Login Component with API integration
const Login = () => {
  const [currentStep, setCurrentStep] = useState("login");
  const [username, setUsername] = useState("admin");
  const [password, setPassword] = useState("admin");
  const [mfaCode, setMfaCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const navigate = useNavigate();
 // Track an optional login txn from the backend
const [loginTxn, setLoginTxn] = useState(null);
// inside the Login component (alongside other handlers)
const handleBypass = () => {
  // DEV-ONLY: mark the session as bypassed
  localStorage.setItem("authToken", "BYPASS_DEBUG_TOKEN");
  // (optional) lightweight demo user info
  localStorage.setItem(
    "demoUser",
    JSON.stringify({ name: "Demo User", role: "admin", bypass: true })
  );
  navigate("/dashboard");
};

const handleLogin = async (e) => {
  e.preventDefault();
  setError("");
  setLoading(true);

  try {
    if (isOrionUsername(username)) {
      const tokenData = await loginOrionUser({ username, password });
      const accessToken = tokenData?.access_token;

      if (!accessToken) {
        setError("Orion login did not return an access token.");
        return;
      }

      let profile = null;
      try {
        profile = await fetchOrionProfile(accessToken);
      } catch (profileError) {
        profile = null;
      }

      const normalizedUser = normalizeOrionUser(profile, username);

      setUserInfo(normalizedUser);
      localStorage.setItem("authToken", accessToken);
      localStorage.setItem("currentUser", JSON.stringify(normalizedUser));
      localStorage.setItem("authProvider", "orion");

      if (tokenData?.refresh_token) {
        localStorage.setItem("orionRefreshToken", tokenData.refresh_token);
      }

      navigate("/dashboard");
      return;
    }

    const endpoint = `/api/auth/login`;
    const params = { username, password };
    const { data } = await axios.get(endpoint, { params });

    // If backend responds with user info, safely keep it in state (not storage)
    if (data?.user) {
      setUserInfo({
        name: data.user.name,
        email: data.user.email,
        role: data.user.role,
      });
    }

    // If MFA is required, DO NOT save token yet
    if (
      data?.mfaRequired === true ||
      data?.status?.toLowerCase?.() === "pending_mfa" ||
      data?.next_step === "mfa"
    ) {
      setLoginTxn(data?.login_txn ?? null);
      setCurrentStep("mfa");
      return;
    }

    // If no MFA and we already have a token, complete login
    const token = data?.access_token || data?.token;
    if (token) {
      localStorage.setItem("authToken", token);
      localStorage.setItem("authProvider", "default");
      navigate("/dashboard");
      return;
    }

    // Otherwise treat as failure
    setError(data?.message || "Login failed. Please check your credentials.");
  } catch (err) {
    setError(
      err?.response?.data?.message ||
      err?.message ||
      "An error occurred during login"
    );
  } finally {
    setLoading(false);
  }
};

const handleMFA = async (e) => {
  e.preventDefault();
  setError("");
  setLoading(true);

  try {
    const endpoint = `/api/auth/mfa`;
    const params = {
      mfa: mfaCode,
      ...(loginTxn ? { login_txn: loginTxn } : {}),
      ...(username ? { username } : {}),
    };

    const { data } = await axios.get(endpoint, { params });

    const ok =
      (typeof data?.status === "string" &&
        data.status.toLowerCase() === "success") ||
      data?.ok === true;

    const token = data?.access_token || data?.token;

    if (ok && token) {
      localStorage.setItem("authToken", token);
      localStorage.setItem("authProvider", "default");
      navigate("/dashboard");
      return;
    }

    if (ok && !token) {
      setError("MFA verified, but no token was returned by the server.");
      return;
    }

    setError(data?.message || "Invalid MFA code. Please try again.");
  } catch (err) {
    setError(
      err?.response?.data?.message ||
      err?.message ||
      "An error occurred during MFA verification"
    );
  } finally {
    setLoading(false);
  }
};


  return (
    <>
      {currentStep === "login" && (
        <LoginForm
          username={username}
          password={password}
          showPassword={showPassword}
          loading={loading}
          error={error}
          setUsername={setUsername}
          setPassword={setPassword}
          setShowPassword={setShowPassword}
          handleLogin={handleLogin}
        />
      )}
      {currentStep === "mfa" && (
        <MFAForm
          mfaCode={mfaCode}
          error={error}
          loading={loading}
          setMfaCode={setMfaCode}
          handleMFA={handleMFA}
          userInfo={userInfo}
        />
      )}
      {(process.env.NODE_ENV === "development" || process.env.REACT_APP_ENABLE_BYPASS === "true") && (
        <button
          type="button"
          onClick={handleBypass}
          style={{
            marginTop: 12,
            width: "100%",
            padding: "10px 14px",
            borderRadius: 8,
            border: "1px dashed var(--cares-line-strong)",
            background: "transparent",
            cursor: "pointer"
          }}
          title="Dev-only shortcut to preview the app"
        >
          Bypass → Go to Dashboard
        </button>
      )}

    </>
  );
};

export default Login;
